import { useMemo } from "react";
import Button from "components/button";
import { Columns, Table } from "components/table";
import { VendorBranchFormValues, VendorBranchItem } from "_interfaces/vendor.interfaces";
import Icon from "components/icon";
import Input from "components/input";
import PhoneInput from "components/input/phone-input";
import { Controller, UseFormReturn } from "react-hook-form";
import { NamespaceKeys } from "locale/types";
import VendorAccordion from "./vendor-accordion";

interface VendorBranchTableRow extends VendorBranchItem {
  index: number;
  rowKey: string;
  fullPhoneNumber: string;
}

interface VendorBranchAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  onAddToList: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  branchItems: VendorBranchItem[];
  editingBranchIndex: number | null;
  form: UseFormReturn<VendorBranchFormValues>;
  t: (key: NamespaceKeys<"customer">, options?: Record<string, unknown>) => string;
}

const VendorBranchAccordion = ({
  isOpen,
  onToggle,
  onNext,
  onAddToList,
  onEdit,
  onDelete,
  branchItems,
  editingBranchIndex,
  form,
  t,
}: VendorBranchAccordionProps) => {
  const {
    register,
    formState: { errors },
  } = form;

  const tableData = useMemo<VendorBranchTableRow[]>(
    () =>
      branchItems.map((item, index) => ({
        ...item,
        index,
        rowKey: `${item.branchName}-${index}`,
        fullPhoneNumber: `${item.picCountryCode} ${item.picPhoneNumber}`,
      })),
    [branchItems],
  );

  const columns: Columns<VendorBranchTableRow>[] = useMemo(
    () => [
      {
        id: "branchName",
        fieldId: "branchName",
        label: t("col-branch-name"),
      },
      {
        id: "picName",
        fieldId: "picName",
        label: t("col-pic-name"),
      },
      {
        id: "picEmail",
        fieldId: "picEmail",
        label: t("col-pic-email"),
      },
      {
        id: "picPhoneNumber",
        fieldId: "fullPhoneNumber",
        label: t("col-pic-phone"),
      },
      {
        id: "action",
        fieldId: "rowKey",
        label: t("col-action"),
        width: "120px",
        dragDisabled: true,
        render: (row) => (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              color="tertiary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => onEdit(row.index)}
            />
            <Button
              type="button"
              variant="outline"
              color="error"
              size="small"
              iconLeft={<Icon name="trash" size={14} />}
              onClick={() => onDelete(row.index)}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <VendorAccordion
      title={t("accordion-vendor-branch")}
      subtitle={t("accordion-vendor-branch-subtitle")}
      iconName="building"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-4">
          <Input
            label={t("label-branch-name")}
            required
            placeholder={t("placeholder-branch-name")}
            data-testid="input-branch-name"
            {...register("branchName")}
            error={errors.branchName}
          />
          <Input
            label={t("label-pic-name")}
            required
            placeholder={t("placeholder-pic-name")}
            {...register("picName")}
            error={errors.picName}
          />
          <div />

          <Input
            label={t("label-pic-email")}
            required
            placeholder={t("placeholder-pic-email")}
            {...register("picEmail")}
            error={errors.picEmail}
          />
          <Controller
            name="picPhoneNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <PhoneInput
                label={t("label-pic-phone-number")}
                required
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                countryCode={form.watch("picCountryCode")}
                onCountryChange={(code) => form.setValue("picCountryCode", code)}
                placeholder={t("placeholder-pic-phone-number")}
                error={fieldState.error}
              />
            )}
          />
          <div className="flex items-end">
            <Button
              type="button"
              variant="fill"
              color="primary"
              onClick={onAddToList}
              data-testid="btn-add-branch"
            >
              {editingBranchIndex !== null ? t("btn-save-changes") : t("btn-add-to-list")}
            </Button>
          </div>
        </div>

        <div className="max-h-[330px] overflow-auto rounded-xl">
          <Table<VendorBranchTableRow>
            data={tableData}
            columns={columns}
            rowKey="index"
            currentPage={1}
            limit={10}
            emptyTitle={t("empty-branch")}
          />
        </div>

        <div className="flex justify-end pt-1">
          <Button type="button" variant="outline" color="tertiary" onClick={onNext}>
            {t("btn-next")}
          </Button>
        </div>
      </div>
    </VendorAccordion>
  );
};

export default VendorBranchAccordion;
